// The print sheet's ground rectangle, and the zoom that fits it on screen.
//
// The server prints an axis-aligned rectangle in the sheet's CRS (Tanger's
// spec — see printScale.ts's groundExtentMetres for the extent, and
// printProjection.ts for the projector and the ground-to-CRS factor): a fixed
// width and height of ground, centred on the point the user picked. Screen
// north and that CRS's grid north only agree on the projection's own central
// meridian; everywhere else there is meridian convergence, so the corners are
// computed in the projected plane and only converted back to lon/lat afterward
// — building the rectangle directly in lon/lat would bake in the on-screen
// map's projection instead of the sheet's.

import { MAP_MAX_ZOOM, MAP_MIN_ZOOM } from '@wehunt/map'
import { FRAME_INSET, type Size } from './printLayout'
import type { PrintProjector } from './printProjection'
import { groundExtentMetres } from './printScale'
import type { PaperFormat, PrintOrientation } from './printPaper'
import type { GeoPt } from './types'

export interface PrintFrameArgs {
  center: GeoPt
  scale: number
  format: PaperFormat
  orientation: PrintOrientation
  /** Margin per edge in mm. Defaults to {@link DEFAULT_PRINT_MARGIN_MM} via `groundExtentMetres`. */
  marginMm?: number
  /** Explicit page millimetres, which win over `format`/`orientation` — see `PrintSheetSpec`. */
  page?: { w: number; h: number }
}

/**
 * The print sheet's four corners, in the CRS the given `projector` transforms
 * to/from — the caller resolves that projector from the print server's probe
 * for the sheet's centre (see `printCrsOf`/`loadPrintProjector`).
 *
 * The rectangle is built axis-aligned around the projected centre (E ± w/2,
 * N ± h/2) and each corner is then inverted back to lon/lat individually,
 * rather than interpolating in lon/lat — a national grid projection is only
 * locally linear, so the two axes only stay perpendicular in the metre plane.
 *
 * Returned counter-clockwise starting bottom-left, as an open ring (the first
 * point is not repeated): bottom-left, bottom-right, top-right, top-left.
 */
export function printFrameCorners(
  projector: PrintProjector,
  { center, scale, format, orientation, marginMm, page }: PrintFrameArgs,
): [GeoPt, GeoPt, GeoPt, GeoPt] {
  const extent = groundExtentMetres({ scale, format, orientation, marginMm, page })
  const [centerE, centerN] = projector.forward(center)
  // Ground metres into the projector's own plane. Unity for a national grid;
  // 1/cos(lat) for the worldwide Mercator fallback, where laying the ground
  // extent out unconverted would draw a frame half its true size at 60°N.
  const k = projector.crsMetresPerGroundMetre(center.lat)
  const halfW = (extent.w * k) / 2
  const halfH = (extent.h * k) / 2

  const [bottomLeft, bottomRight, topRight, topLeft]: [number, number][] = [
    [centerE - halfW, centerN - halfH],
    [centerE + halfW, centerN - halfH],
    [centerE + halfW, centerN + halfH],
    [centerE - halfW, centerN + halfH],
  ]

  return [
    projector.inverse(bottomLeft),
    projector.inverse(bottomRight),
    projector.inverse(topRight),
    projector.inverse(topLeft),
  ]
}

/**
 * The corners' axis-aligned bounding box in screen pixels, under the caller's
 * own `project` function (the map's current screen projection). Separate from
 * `zoomToFitFrame` because it is the measurement the fit is DEFINED by: at the
 * zoom the fit returns, this box lands on the inset. That is not observable
 * from the returned zoom number alone, so the fit's own tests measure it here.
 */
export function frameBoundingBoxPx(
  corners: GeoPt[],
  project: (p: GeoPt) => [number, number],
): Size {
  const pts = corners.map(project)
  const xs = pts.map(([x]) => x)
  const ys = pts.map(([, y]) => y)
  return {
    w: Math.max(...xs) - Math.min(...xs),
    h: Math.max(...ys) - Math.min(...ys),
  }
}

/**
 * The zoom level that fits the print frame's corners inside `inset` of the
 * container, on both axes.
 *
 * `project` is the map's own screen projection at `currentZoom` (MapLibre's,
 * for the real caller), so the current bounding box is exact rather than
 * approximated from lon/lat spans. A map's pixels-per-projected-metre exactly
 * doubles per zoom level, so scaling the *current* box by the ratio needed on
 * the binding axis and converting that ratio to a zoom delta via log2 is exact
 * too — no iterative search needed, unlike fitting arbitrary lon/lat bounds
 * across a projection that isn't a simple power-of-two scale.
 */
export function zoomToFitFrame({
  corners,
  containerPx,
  inset = FRAME_INSET,
  currentZoom,
  project,
}: {
  corners: GeoPt[]
  containerPx: Size
  inset?: number
  currentZoom: number
  project: (p: GeoPt) => [number, number]
}): number {
  const bbox = frameBoundingBoxPx(corners, project)
  // A degenerate box on ONE axis simply can't bind there, and the other axis
  // still decides the fit — Infinity drops it out of the `min` below.
  const scaleForW = bbox.w > 0 ? (inset * containerPx.w) / bbox.w : Infinity
  const scaleForH = bbox.h > 0 ? (inset * containerPx.h) / bbox.h : Infinity
  // Degenerate on BOTH axes there is nothing to fit to, and the arithmetic
  // would say `log2(Infinity)` — i.e. zoom all the way in on a point, which
  // the clamp below would happily deliver as MAP_MAX_ZOOM. Staying put is the
  // honest answer: the caller asked to frame a rectangle that has no extent.
  if (scaleForW === Infinity && scaleForH === Infinity) return currentZoom
  const zoom = currentZoom + Math.log2(Math.min(scaleForW, scaleForH))
  return Math.min(MAP_MAX_ZOOM, Math.max(MAP_MIN_ZOOM, zoom))
}
